import React, { Suspense, Component } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { AuthProvider, useAuth } from 'react-oidc-context';
import i18n from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import { DraggableContainer, en as helixTranslations, HELIX_REACT_AI_TRANSLATION_NAMESPACE } from '@ideagen/helix-react-ai';
import sessionManager from '../../store/modules/session/sessionManager'
import store from '../../store'
import { attachMazlanTracking } from './mazlanTracking.js';

// Simplified AuthProvider that provides a basic auth context
const SimpleAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProvider
      authority="https://login.microsoftonline.com/common/v2.0"
      client_id="mock-client-id"
      redirect_uri={window.location.origin}
      automaticSilentRenew={false}
      skipSigninCallback={true}
      loadUserInfo={false}
    >
      <AuthUserProvider>
        {children}
      </AuthUserProvider>
    </AuthProvider>
  );
};

// Stable component that provides user data to the auth context
const AuthUserProvider: React.FC<{ children: React.ReactNode }> = React.memo(({ children }) => {
  const auth = useAuth();
  
  // Use useRef to avoid re-renders and component reloads 
  const initializationRef = React.useRef(false);
  
  React.useEffect(() => {
    if (!initializationRef.current && auth && typeof auth.signinSilent === 'function') {
      initializationRef.current = true;
      
      // Create async function to handle token dispatch
      const initializeAuth = async () => {
        try {
          const isHubEnabled = store.getters['hub/isEnabled'];
          const token =
            (await store.dispatch(isHubEnabled ? 'hub-session/getToken' : 'cs-session/getToken')) ||
            '1234567890';
          const userProfile = store.getters['session/user'];
          
          // Create a simple mock user and inject it
          const mockUser = {
            id_token: token,
            access_token: token,
            profile: {
              name: userProfile?.displayName || userProfile?.name?.givenName || 'User',
              email: userProfile?.email || 'user@example.com',
              sub: userProfile?.email || 'user-123'
            },
            expired: false,
            expires_at: Math.floor(Date.now() / 1000) + 3600
          };
          
          // Set the user directly in localStorage for oidc-client to pick up
          const userKey = `oidc.user:${window.location.origin}:mock-client-id`;
          localStorage.setItem(userKey, JSON.stringify(mockUser));
          
          // Force the auth context to reload the user without timeout
          if ((auth as any)._userManager?.getUser) {
            (auth as any)._userManager.getUser();
          }
        } catch (error) {
          console.warn('Could not initialize auth or set user in localStorage:', error);
        }
      };
      
      // Execute the async initialization
      initializeAuth();
    }
  }, [auth]); // Remove initialized from deps to prevent re-renders
  
  return React.createElement(React.Fragment, null, children);
});

// Stable error boundary wrapper for React components in Vue
const ReactErrorBoundary: React.FC<{ children: React.ReactNode }> = React.memo(({ children }) => {
  // Use ref to avoid state-based re-renders that cause component reloads
  const errorRef = React.useRef(false);
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  React.useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('React Error in Vue context:', error);
      if (!errorRef.current) {
        errorRef.current = true;
        forceUpdate(); // Force update once without causing full reload
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []); // Empty deps to prevent re-renders

  if (errorRef.current) {
    return React.createElement('div', 
      { 
        style: { 
          padding: '20px', 
          backgroundColor: '#f5f5f5', 
          border: '1px solid #ddd', 
          borderRadius: '4px',
          textAlign: 'center' as const
        } 
      },
      'AI Assistant temporarily unavailable'
    );
  }

  return React.createElement(React.Fragment, null, children);
});


const DraggableContainerWrapper: React.FC<any> = React.memo((props) => {
  return React.createElement(DraggableContainer, props);
}, (prevProps, nextProps) => {
  // Optimized comparison - only re-render when props actually change
  return (
    prevProps.open === nextProps.open &&
    prevProps.dataId === nextProps.dataId &&
    prevProps.token === nextProps.token &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.isEmbedded === nextProps.isEmbedded &&
    JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config)
  );
});

// TypeScript interfaces
interface ConnectorProps {
  open?: boolean;
  onClose?: () => void;
  token?: string; // Token passed from Vue as default value
  hasMazlanAdminFeaturesAccess?: boolean;
}

interface MazlanConfig {
  agent_url: string;
  welcome_message: string;
  custom_settings: {
    'ce-app-api-url': string;
    'ce-app-api-authorization': string;
    'ce-app-x-site-uri': string;
  };
  enable_voice: boolean;
  enable_dictation: boolean;
  enable_regional_jurisdictions: boolean;
  enable_document_upload: boolean;
}

interface AppProps {
  open?: boolean;
  onClose?: () => void;
  vueToken?: string; // Token passed from Vue
  hasMazlanAdminFeaturesAccess?: boolean;
}

// Initialize i18n with helix translations
i18n.use(initReactI18next).init({
  lng: 'en',
  fallbackLng: 'en',
  resources: {
    en: {
      translation: {
        // Basic translations - add more as needed
        'loading': 'Loading...',
        'close': 'Close',
        'error': 'Error'
      },
      [HELIX_REACT_AI_TRANSLATION_NAMESPACE]: helixTranslations
    }
  },
  interpolation: {
    escapeValue: false
  }
});

/**
 * Inner component that uses auth context - memoized to prevent unnecessary re-renders
 */
const AuthenticatedApp = React.memo<AppProps>(({ open = false, onClose, vueToken, hasMazlanAdminFeaturesAccess = false }) => {
  const auth = useAuth();

  // Memoize the close handler to prevent re-renders
  const handleClose = React.useCallback((): void => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Get token with Vue token as default/fallback - start with vueToken for initial render
  const [storeToken, setStoreToken] = React.useState<string | null>(vueToken || null);

  React.useEffect(() => {
    const fetchTokenFromStore = async () => {
      try {
        // Use Vue store dispatch to get token
        const isHubEnabled = store.getters['hub/isEnabled'];
        const token = await store.dispatch(
          isHubEnabled ? 'hub-session/getToken' : 'cs-session/getToken'
        );
        setStoreToken(token || vueToken); // Use Vue token as fallback
      } catch (error) {
        console.warn('Failed to dispatch token action in Connector.tsx:', error);
        // Fallback to Vue token if dispatch fails
        if (vueToken) {
          setStoreToken(vueToken);
        }
      }
    };

    fetchTokenFromStore();
  }, [vueToken]); // Re-run when Vue token changes

  // Memoize the config to prevent re-renders
  const mazlanConfig: MazlanConfig = React.useMemo(() => {
    return {
    agent_url:  process.env.MAZLAN_AGENT_URL,
    welcome_message: "Hi I'm Mazlan, \n\nI’m here to make working with your policies easier — whether you want to create, improve, review, or summarise.\n\nPlease choose what you’d like to do first:",
    custom_settings: {
      "ce-app-api-url": process.env.CONTENT_ENGINE_URL || '',
      "ce-app-x-site-uri": sessionManager.getXSiteUrl() || '',
      "ce-app-api-authorization": `Bearer ${storeToken}`
    },
    // helix-react-ai v3.x renamed the voice flags: voice_mode_enabled -> enable_voice
    // (now also gates the TTS audio button, replacing the removed standalone tts flag),
    // transcription_enabled -> enable_dictation.
    enable_voice: process.env.VOICE_MODE_ENABLED === 'true',
    enable_dictation: process.env.TRANSCRIPTION_ENABLED === 'true',
    enable_regional_jurisdictions: process.env.ENABLE_REGIONAL_JURISDICTIONS === 'true',
    enable_document_upload: process.env.ENABLE_DOCUMENT_UPLOAD === 'true',
    };
  }, [storeToken, vueToken]); // Update when tokens change

  // Track the real Mazlan session/thread ID provided by helix-react-ai.
  const [mazlanSessionId, setMazlanSessionId] = React.useState<string | null>(null);

  // Document-level Mazlan Gainsight tracking. The chat UI (textarea, chips) is
  // portaled to document.body by helix-react-ai, so we listen on `document` and
  // match stable `data-id` selectors. See mazlanTracking.js for details.
  // Only tracked while the dialog is open, so the global context doesn't leak
  // into events from other pages after Mazlan is closed. Re-runs when the chat
  // session switches, so the global context always has the current session ID.
  React.useEffect(
    () => attachMazlanTracking({ open, hasMazlanAdminFeaturesAccess, sessionId: mazlanSessionId }),
    [open, hasMazlanAdminFeaturesAccess, mazlanSessionId]
  );

  // Use token with multiple fallbacks: store dispatch -> Vue prop -> auth context -> default
  const finalToken = React.useMemo(() => {
    const token = storeToken || vueToken || auth?.user?.id_token;
    return token;
  }, [storeToken, vueToken, auth?.user?.id_token]);

  return (
    <ReactErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <DraggableContainerWrapper
          dataId="draggable-container-sample"
          open={open}
          onClose={handleClose}
          token={finalToken}
          config={mazlanConfig}
          onSessionChange={setMazlanSessionId}
          onFeedbackClick={() => {}}
          isEmbedded={false}
        />
      </I18nextProvider>
    </ReactErrorBoundary>
  );
});

/**
 * Main App component with DraggableContainer - memoized to prevent re-renders
 */
const App = React.memo<AppProps>(({ open = false, onClose, vueToken, hasMazlanAdminFeaturesAccess }) => {
  // Use simple auth provider that injects user data from Vue store
  return (
    <SimpleAuthProvider>
      <AuthenticatedApp open={open} onClose={onClose} vueToken={vueToken} hasMazlanAdminFeaturesAccess={hasMazlanAdminFeaturesAccess} />
    </SimpleAuthProvider>
  );
});

/**
 * Autonomous React App that manages its own lifecycle and state
 */
class AutonomousReactApp {
  private root: Root;
  private isOpen: boolean = false;
  private onCloseCallback: (() => void) | null = null;
  private vueToken: string | null = null;
  private hasMazlanAdminFeaturesAccess: boolean = false;

  constructor(targetEl: HTMLElement) {
    // Simple React root without complex configuration
    this.root = createRoot(targetEl, {
      identifierPrefix: 'mazlan-',
      // Minimal production configuration
      ...(process.env.NODE_ENV === 'production' && {
        onRecoverableError: (error, errorInfo) => {
          console.warn('React error:', error);
        }
      })
    });

    // React mounts itself immediately and manages its own state
    this.renderApp();
  }

  // Vue calls this to update React's internal state
  updateState(isOpen: boolean, onClose?: () => void, token?: string, hasMazlanAdminFeaturesAccess?: boolean): void {
    this.isOpen = isOpen;
    this.onCloseCallback = onClose || null;
    this.vueToken = token || null;
    this.hasMazlanAdminFeaturesAccess = hasMazlanAdminFeaturesAccess ?? false;
    this.renderApp(); // Re-render with new state
  }

  private renderApp(): void {
    try {
      // React handles everything internally - Vue just provides state updates
      this.root.render(
        <App 
          open={this.isOpen} 
          vueToken={this.vueToken || undefined}
          hasMazlanAdminFeaturesAccess={this.hasMazlanAdminFeaturesAccess}
          onClose={() => {
            // React handles the close internally
            this.isOpen = false;
            this.renderApp(); // Close the dialog in React
            
            // Then notify Vue
            if (this.onCloseCallback) {
              this.onCloseCallback();
            }
          }} 
        />
      );
    } catch (error) {
      console.error('Failed to render React component:', error);
      this.root.render(
        React.createElement('div', 
          { style: { padding: '20px', textAlign: 'center' } }, 
          'AI Assistant temporarily unavailable'
        )
      );
    }
  }

  unmount(): void {
    try {
      this.root.unmount();
    } catch (error) {
      console.warn('Error during React unmount:', error);
    }
  }
}

/**
 * Simple connector interface for Vue - React manages itself
 */
export class ReactConnector {
  private app: AutonomousReactApp;

  constructor(targetEl: HTMLElement) {
    // React app starts immediately and manages its own lifecycle
    this.app = new AutonomousReactApp(targetEl);
  }

  // Simplified API - Vue just tells React what state it should be in
  render(props: ConnectorProps = {}): void {
    this.app.updateState(props.open || false, props.onClose, props.token, props.hasMazlanAdminFeaturesAccess);
  }

  unmount(): void {
    this.app.unmount();
  }
}

export default App;
